import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Container,
	Heading,
	Heading3,
	MultiSelect,
	Paragraph,
	Pill,
	Select,
	Separator,
	Switch,
	UnorderedList,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import ProductTile from '../ProductTile';
import StyrCompetencyMapping from './StyrCompetencyMapping';
import StyrJobMappingTable from './StyrJobMappingTable';

function formatLastSyncedAt(iso: string): string {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return iso;
	}

	return new Intl.DateTimeFormat('nl-NL', {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(parsed);
}

const STYR_LOGO_URL = '/images/marketplace/styr.png';

const Banner = styled(Box)`
	background: linear-gradient(135deg, #f4f8fb 0%, #e7eef5 100%);
	border: 1px solid ${tokens.border.subtle};
	border-radius: 8px;
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`;

const BannerLogo = styled.img`
	max-height: 88px;
	max-width: 320px;
	object-fit: contain;
`;

const ConfigCard = styled(Box)`
	border: 1px solid ${tokens.border.subtle};
	border-radius: 6px;
	background: ${(props) => props.theme.colors.white};
	padding: 20px 24px;
`;

const ConfigRow = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	padding: 10px 0;
	gap: 16px;
`;

const ConfigLabel = styled.span`
	color: ${tokens.text.secondary};
	opacity: 0.7;
	font-size: 14px;
`;

const ConfigValue = styled.span`
	color: ${tokens.text.secondary};
	font-size: 14px;
	font-weight: 500;
	text-align: right;
`;

const ActivePill = styled(Pill)`
	background-color: ${(props) => props.theme.colors.green};
	color: ${(props) => props.theme.colors.white};
`;

const InactivePill = styled(Pill)`
	background-color: ${tokens.border.subtle};
	color: ${tokens.text.secondary};
`;

function Description() {
	const { t } = useTranslation();

	return (
		<>
			<Paragraph mb={2}>{t('settings.connectors.styr.description.intro')}</Paragraph>
			<Paragraph mb={3}>{t('settings.connectors.styr.description.sync')}</Paragraph>
			<Heading3>{t('settings.connectors.styr.description.benefitsTitle')}</Heading3>
			<Paragraph>
				<UnorderedList>
					{t('settings.connectors.styr.benefits')
						.split('\n')
						.map((benefit) => benefit.trim())
						.filter(Boolean)
						.map((benefit) => (
							<li key={benefit}>{benefit}</li>
						))}
				</UnorderedList>
			</Paragraph>
		</>
	);
}

function sameLanguageIDs(left: string[], right: string[]): boolean {
	return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * Selects which Styr languages are written during sync. Saves immediately on change; an empty
 * selection means every language (the default). Takes effect on the next sync.
 * @param props - Component props.
 * @param props.languageIDs - The currently saved sync language filter.
 */
function SyncLanguagesSelect(props: { languageIDs: string[] }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { meta } = useLocalize();

	const [languageIDs, setLanguageIDs] = useState(props.languageIDs);
	const savedLanguageIDsRef = useRef(props.languageIDs);
	const languageIDsRef = useRef(props.languageIDs);
	languageIDsRef.current = languageIDs;
	const savingRef = useRef(false);
	const [setStyrSyncLanguages] = graphql.useSetStyrSyncLanguagesMutation();

	useEffect(() => {
		if (sameLanguageIDs(props.languageIDs, savedLanguageIDsRef.current)) {
			return;
		}

		if (!sameLanguageIDs(languageIDsRef.current, savedLanguageIDsRef.current)) {
			return;
		}

		savedLanguageIDsRef.current = props.languageIDs;
		languageIDsRef.current = props.languageIDs;
		setLanguageIDs(props.languageIDs);
	}, [props.languageIDs]);

	const options = meta.treamsLanguages
		.map((value) => ({ title: t(`common.language.${value}`), value }))
		.sort((a, b) => a.title.localeCompare(b.title));

	const persistLatest = useCallback(async () => {
		if (savingRef.current) {
			return;
		}

		if (sameLanguageIDs(languageIDsRef.current, savedLanguageIDsRef.current)) {
			return;
		}

		savingRef.current = true;

		try {
			while (!sameLanguageIDs(languageIDsRef.current, savedLanguageIDsRef.current)) {
				const toSave = languageIDsRef.current;

				await setStyrSyncLanguages({
					variables: { languageIDs: toSave },
					update(cache) {
						cache.updateQuery({ query: graphql.ViewerStyrConfigDocument }, (data) => {
							if (!data?.viewer?.workspace?.styrConfig) {
								return data;
							}

							return {
								...data,
								viewer: {
									...data.viewer,
									workspace: {
										...data.viewer.workspace,
										styrConfig: {
											...data.viewer.workspace.styrConfig,
											languageIDs: toSave,
										},
									},
								},
							};
						});
					},
				});

				savedLanguageIDsRef.current = toSave;
			}

			addToast(t('settings.connectors.styr.config.languages.saved'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			languageIDsRef.current = savedLanguageIDsRef.current;
			setLanguageIDs(savedLanguageIDsRef.current);
			addToast(t('settings.connectors.styr.config.languages.failed'), {
				appearance: 'error',
				autoDismiss: true,
			});
		} finally {
			savingRef.current = false;

			if (!sameLanguageIDs(languageIDsRef.current, savedLanguageIDsRef.current)) {
				void persistLatest();
			}
		}
	}, [addToast, setStyrSyncLanguages, t]);

	useEffect(() => {
		void persistLatest();
	}, [languageIDs, persistLatest]);

	return (
		<Box>
			<MultiSelect
				selectedOptions={options.filter(({ value }) => languageIDs.includes(value))}
				options={options}
				onChange={setLanguageIDs}
			/>
			<Paragraph small mt={1}>
				{t('settings.connectors.styr.config.languages.hint')}
			</Paragraph>
		</Box>
	);
}

/**
 * The level set Styr creates competencies on, and the opt-in sync of Styr job families and
 * groups into expertises and groeipaden. Both save immediately and apply on the next sync.
 * @param props - Component props.
 * @param props.competencyLevelSetID - The configured creation level set; empty means automatic.
 * @param props.syncGrowthPaths - Whether the sync maintains expertises and groeipaden.
 */
function StyrGrowthPathSettings(props: {
	competencyLevelSetID?: string;
	syncGrowthPaths: boolean;
}) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();

	const levelSetsQuery = graphql.useCompetencyLevelSetsQuery({
		variables: { isArchived: false, first: 1000 },
	});
	const scaleQuery = graphql.useGrowthPathScaleQuery();
	const growthPathLevelSetID =
		scaleQuery.data?.viewer?.workspace?.growthPathScale?.competencyLevelSetID;

	const refetchQueries = [{ query: graphql.ViewerStyrConfigDocument }];
	const [setLevelSet, { loading: savingLevelSet }] = graphql.useSetStyrCompetencyLevelSetMutation({
		refetchQueries,
	});
	const [setSyncGrowthPaths, { loading: savingSync }] = graphql.useSetStyrSyncGrowthPathsMutation({
		refetchQueries,
	});

	const options = (levelSetsQuery.data?.competencyLevelSets.edges ?? [])
		.filter(({ node }) => node.id !== growthPathLevelSetID)
		.map(({ node }) => ({ value: node.id, title: localize(node.title).text }));

	const save = async (mutate: () => Promise<unknown>) => {
		try {
			await mutate();
			addToast(t('settings.connectors.styr.config.saved'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			addToast(t('settings.connectors.styr.config.saveFailed'), {
				appearance: 'error',
				autoDismiss: true,
			});
		}
	};

	return (
		<>
			<ConfigRow>
				<ConfigLabel>{t('settings.connectors.styr.config.levelSet')}</ConfigLabel>
				<Box flexGrow={1} style={{ maxWidth: 340 }}>
					<Select
						clearable
						options={options}
						selectedValue={props.competencyLevelSetID}
						placeholder={t('settings.connectors.styr.config.levelSet.automatic')}
						disabled={savingLevelSet}
						onChange={(value) =>
							save(() => setLevelSet({ variables: { competencyLevelSetID: value ?? null } }))
						}
					/>
					<Paragraph small mt={1}>
						{t('settings.connectors.styr.config.levelSet.hint')}
					</Paragraph>
				</Box>
			</ConfigRow>
			<ConfigRow>
				<ConfigLabel>{t('settings.connectors.styr.config.syncGrowthPaths')}</ConfigLabel>
				<Box flexGrow={1} style={{ maxWidth: 340 }}>
					<Switch
						on={props.syncGrowthPaths}
						disabled={savingSync}
						onChange={(enabled) => save(() => setSyncGrowthPaths({ variables: { enabled } }))}
					/>
					<Paragraph small mt={1}>
						{t('settings.connectors.styr.config.syncGrowthPaths.hint')}
					</Paragraph>
				</Box>
			</ConfigRow>
		</>
	);
}

/**
 * Customer-facing Styr integrations page. Read-only by design \u2014 there is no enable/disable
 * toggle here; activation is managed exclusively by Treams support from the launch
 * environment (`WorkspaceStyr` panel). The customer-facing actions are "Synchroniseer nu"
 * (on-demand sync) and the sync language filter.
 */
export default function Styr() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { localize } = useLocalize();

	const query = graphql.useViewerStyrConfigQuery({
		fetchPolicy: 'cache-and-network',
	});

	const styrConfig = query.data?.viewer?.workspace?.styrConfig ?? null;
	const isActive = !!styrConfig?.enabled;

	const styrOrganisationDisplay = styrConfig?.organisationLabel || styrConfig?.organisationID || '';

	const lastSyncedAtDisplay = styrConfig?.lastSyncedAt
		? formatLastSyncedAt(styrConfig.lastSyncedAt)
		: null;

	const competenciesQuery = graphql.useCompetenciesQuery({
		variables: { isArchived: false, first: 1000 },
		fetchPolicy: 'cache-and-network',
		skip: !styrConfig,
	});

	const linkedCompetencies = (competenciesQuery.data?.competencies?.edges ?? [])
		.flatMap((edge) => (edge?.node ? [edge.node] : []))
		.filter((node) => !!node.styrSkillID)
		.map((node) => ({ id: node.id, title: localize(node.title).text }));

	const [syncStyr, { loading: syncing }] = graphql.useSyncStyrMutation();

	const handleSync = async () => {
		try {
			await syncStyr();
			addToast(t('settings.connectors.styr.sync.started'), {
				appearance: 'success',
				autoDismiss: true,
			});
		} catch {
			addToast(t('settings.connectors.styr.sync.failed'), {
				appearance: 'error',
				autoDismiss: true,
			});
		}
	};

	return (
		<Container py={4}>
			<Banner mb={3}>
				<BannerLogo src={STYR_LOGO_URL} alt="Styr" />
			</Banner>

			<Flex mb={3} alignItems="center">
				<Heading flexGrow={1} flexShrink={1}>
					Styr
				</Heading>
				<Box flexGrow={0} flexShrink={0}>
					{isActive ? (
						<ActivePill small>{t('settings.connectors.styr.status.active')}</ActivePill>
					) : (
						<InactivePill small>{t('settings.connectors.styr.status.inactive')}</InactivePill>
					)}
				</Box>
			</Flex>

			<Flex mb={4} style={{ gap: 24 }}>
				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.styr.description.heading')}</Heading3>
					<Separator mt={2} mb={3} />
					<Description />
					<Paragraph mb={2}>
						<a
							href="https://styr.nl/"
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'underline' }}
						>
							{t('settings.connectors.styr.viewWebsite')}
						</a>
					</Paragraph>
				</Box>

				<Box width={[1, 1, 1 / 2]}>
					<Heading3 mb={1}>{t('settings.connectors.styr.config.title')}</Heading3>
					<Separator mt={2} mb={3} />
					<ConfigCard>
						<ConfigRow>
							<ConfigLabel>{t('settings.connectors.styr.config.status')}</ConfigLabel>
							<ConfigValue>
								{isActive ? (
									<ActivePill small>{t('settings.connectors.styr.status.active')}</ActivePill>
								) : (
									<InactivePill small>{t('settings.connectors.styr.status.inactive')}</InactivePill>
								)}
							</ConfigValue>
						</ConfigRow>
						<ConfigRow>
							<ConfigLabel>{t('settings.connectors.styr.config.organisation')}</ConfigLabel>
							<ConfigValue>{styrOrganisationDisplay || '—'}</ConfigValue>
						</ConfigRow>
						<ConfigRow>
							<ConfigLabel>{t('settings.connectors.styr.config.languages')}</ConfigLabel>
							{isActive ? (
								<Box flexGrow={1} style={{ maxWidth: 340 }}>
									<SyncLanguagesSelect languageIDs={styrConfig?.languageIDs ?? []} />
								</Box>
							) : (
								<ConfigValue>
									{styrConfig?.languageIDs?.length
										? styrConfig.languageIDs
												.map((languageID) => t(`common.language.${languageID}`))
												.join(', ')
										: t('settings.connectors.styr.config.languages.all')}
								</ConfigValue>
							)}
						</ConfigRow>
						{isActive && (
							<StyrGrowthPathSettings
								competencyLevelSetID={styrConfig?.competencyLevelSet?.id}
								syncGrowthPaths={!!styrConfig?.syncGrowthPaths}
							/>
						)}
						<ConfigRow>
							<ConfigLabel>{t('settings.connectors.styr.config.lastSynced')}</ConfigLabel>
							<ConfigValue>
								{lastSyncedAtDisplay || t('settings.connectors.styr.config.neverSynced')}
							</ConfigValue>
						</ConfigRow>

						{!styrConfig && (
							<Paragraph small mt={2}>
								{t('settings.connectors.styr.config.notConfigured')}
							</Paragraph>
						)}

						<Separator my={3} />

						<Flex justifyContent="flex-end">
							<Button
								type="primary"
								title={t('settings.connectors.styr.syncNow')}
								loading={syncing}
								disabled={!isActive || syncing}
								onClick={handleSync}
							/>
						</Flex>
					</ConfigCard>
				</Box>
			</Flex>

			{styrConfig && (
				<Box mt={2}>
					<StyrJobMappingTable />
				</Box>
			)}

			{styrConfig && linkedCompetencies.length > 0 && (
				<Box mt={4}>
					<StyrCompetencyMapping competencies={linkedCompetencies} />
				</Box>
			)}
		</Container>
	);
}

export function StyrTile(props: { selected: boolean }) {
	const { t } = useTranslation();

	return (
		<ProductTile
			id="styr"
			title="Styr"
			description={t('settings.connectors.styr.tile.description')}
			logoImageURL={STYR_LOGO_URL}
			selected={props.selected}
			price={75}
		/>
	);
}
